import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  MenuItem,
  Select,
  InputAdornment,
  InputLabel,
  Switch,
  Card,
  CardContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Info as InfoIcon,
  Percent as PercentIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface ChargesDiscountsStepProps {
  invoiceData: any;
  updateInvoiceData: (data: any) => void;
}

const ChargesDiscountsStep: React.FC<ChargesDiscountsStepProps> = ({
  invoiceData,
  updateInvoiceData,
}) => {
  // Local state for discount
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(
    invoiceData.discountType || 'percentage'
  );
  const [discountValue, setDiscountValue] = useState<number>(
    invoiceData.discountValue || 0
  );
  
  // Additional fields
  const [notes, setNotes] = useState<string>(
    invoiceData.notes || ''
  );
  const [termsAndConditions, setTermsAndConditions] = useState<string>(
    invoiceData.termsAndConditions || 'Payment is due within 15 days'
  );
  
  // Local state for calculations
  const [subtotal, setSubtotal] = useState<number>(0);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  
  // Updates invoice data when local state changes
  useEffect(() => {
    updateInvoiceData({
      discountType,
      discountValue,
      notes,
      termsAndConditions,
    });
  }, [
    discountType,
    discountValue,
    notes,
    termsAndConditions,
    updateInvoiceData,
  ]);
  
  // Calculate totals when items or discount changes
  useEffect(() => {
    if (!invoiceData.items) return;
    
    // Calculate subtotal
    const calculatedSubtotal = invoiceData.items.reduce(
      (total: number, item: any) => total + (item.amount || 0),
      0
    );
    
    // Calculate discount amount
    let calculatedDiscountAmount = 0;
    if (discountType === 'percentage') {
      calculatedDiscountAmount = calculatedSubtotal * (discountValue / 100);
    } else {
      calculatedDiscountAmount = discountValue;
    }
    
    // Ensure discount doesn't exceed subtotal
    calculatedDiscountAmount = Math.min(calculatedDiscountAmount, calculatedSubtotal);
    
    // Calculate taxable amount (post-discount)
    const taxableAmount = calculatedSubtotal - calculatedDiscountAmount;
    
    // Calculate total - tax is calculated at the item level
    const calculatedTotal = taxableAmount;
    
    // Update state
    setSubtotal(calculatedSubtotal);
    setDiscountAmount(calculatedDiscountAmount);
    setTotal(calculatedTotal);
  }, [invoiceData.items, discountType, discountValue]);
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };
  
  // Handle discount type change
  const handleDiscountTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountType(event.target.value as 'percentage' | 'fixed');
    // Reset discount value when changing type
    setDiscountValue(0);
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Discounts & Charges
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left column - Discount & Tax */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Discount
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <RadioGroup
                row
                name="discount-type"
                value={discountType}
                onChange={handleDiscountTypeChange}
              >
                <FormControlLabel
                  value="percentage"
                  control={<Radio />}
                  label="Percentage"
                />
                <FormControlLabel
                  value="fixed"
                  control={<Radio />}
                  label="Fixed Amount"
                />
              </RadioGroup>
            </FormControl>
            
            <TextField
              fullWidth
              label={discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {discountType === 'percentage' ? '%' : '₹'}
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: discountType === 'percentage' ? 0.01 : 1,
                max: discountType === 'percentage' ? 100 : subtotal,
              }}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Discount Amount: {formatCurrency(discountAmount)}
            </Typography>
          </Paper>
        </Grid>
        
        {/* Right column - Notes & T&C */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Notes
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Add any notes for the client (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Paper>
          
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Terms & Conditions
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Add terms and conditions (optional)"
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
            />
          </Paper>
        </Grid>
      </Grid>
      
      {/* Summary section */}
      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Invoice Summary
        </Typography>
        
        <TableContainer>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>Subtotal</TableCell>
                <TableCell align="right">{formatCurrency(subtotal)}</TableCell>
              </TableRow>
              
              {discountAmount > 0 && (
                <TableRow>
                  <TableCell>
                    Discount
                    {discountType === 'percentage' && discountValue > 0 && ` (${discountValue}%)`}
                  </TableCell>
                  <TableCell align="right">-{formatCurrency(discountAmount)}</TableCell>
                </TableRow>
              )}
              
              <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{formatCurrency(total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ChargesDiscountsStep; 